import React, { useState, useEffect } from "react";
import { Button, Input, Tag, Switch, Image, message } from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { articleAPIWithErrorHandling, articleHelpers } from "../../../api/article/article";
import dayjs from "dayjs";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
  "thumbnail_url",
  "category",
  "author",
  "publish_date",
  "status",
];

export default function ArticleTable() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const fetchArticles = async (page = 1, pageSize = 10, search = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pageSize,
        search: search.trim(),
      };
      const response = await articleAPIWithErrorHandling.getArticles(params);
      const { articles: articleData, pagination: paginationData } =
        articleHelpers.extractArticleList(response);

      setArticles(articleData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách bài viết");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (paginationConfig) => {
    fetchArticles(paginationConfig.current, paginationConfig.pageSize, searchText);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchArticles(1, pagination.pageSize, value);
  };

  const handleReset = () => {
    setSearchText("");
    fetchArticles(1, pagination.pageSize, "");
  };

  const handleToggleStatus = async (id) => {
    try {
      await articleAPIWithErrorHandling.toggleArticleStatus(id);
      message.success("Cập nhật trạng thái thành công");
      fetchArticles(pagination.current, pagination.pageSize, searchText);
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleDelete = async (record) => {
    if (await confirmDelete({ entity: "bài viết", name: record.title })) {
      try {
        await articleAPIWithErrorHandling.deleteArticle(record.id);
        message.success("Xóa bài viết thành công");
        fetchArticles(pagination.current, pagination.pageSize, searchText);
      } catch (error) {
        message.error(error.message);
      }
    }
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "thumbnail_url", label: "Ảnh bìa",
      column: {
        title: "Ảnh bìa", dataIndex: "thumbnail_url", key: "thumbnail_url", width: 120,
        render: (url) =>
          url ? <Image src={url} alt="thumbnail" width={80} className="rounded" /> : <Tag>No image</Tag>,
      },
    },
    {
      key: "title", label: "Tiêu đề", fixed: true,
      column: {
        title: "Tiêu đề", dataIndex: "title", key: "title",
        ellipsis: true,
        sorter: (a, b) => a.title.localeCompare(b.title),
      },
    },
    {
      key: "category", label: "Danh mục",
      column: {
        title: "Danh mục", dataIndex: "category", key: "category", width: 150,
        render: (category) => category?.name || <Tag color="default">Chưa phân loại</Tag>,
      },
    },
    {
      key: "author", label: "Tác giả",
      column: {
        title: "Tác giả", dataIndex: "author", key: "author", width: 150,
        render: (text) => text || "-",
      },
    },
    {
      key: "publish_date", label: "Ngày đăng",
      column: {
        title: "Ngày đăng", dataIndex: "publish_date", key: "publish_date", width: 120,
        render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", width: 120,
        render: (status, record) => (
          <Switch
            checked={!!status}
            onChange={() => handleToggleStatus(record.id)}
            checkedChildren="Bật"
            unCheckedChildren="Tắt"
          />
        ),
      },
    },
    actionColumnDef(
      (record) => [
        { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => navigate(`/articles/${record.id}`) },
        { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
      ],
      { width: 100 }
    ),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm bài viết..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!searchText;

  return (
    <BrandedPageLayout
      title="Bài viết"
      subtitle="Quản lý danh sách bài viết, tin tức."
      breadcrumbItems={[{ title: "Bài viết" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchArticles(pagination.current, pagination.pageSize, searchText)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/store-article")}>
            Thêm tin tức
          </Button>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={articles}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có dữ liệu"
      />
    </BrandedPageLayout>
  );
}
